import { css, html, LitElement, PropertyValues } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import ms from 'ms';

// The curve is sampled into keyframes rather than evaluated on a timer. Sampling
// is quadratic in the index so the fast opening moves get most of the keyframes.
const SAMPLE_COUNT = 48;
const CREEP_SPAN = ms('5 minutes');

/** How long the bar takes to run out to full once `finishAt` is reached. */
export const FINISH_DURATION = ms('300ms');

/**
 * A progress bar for work that cannot report progress. It fills along
 * `ceiling * t / (t + halfLife)`: quick off the line, and slower with every
 * second that passes, approaching `ceiling` without ever arriving. Setting
 * `finishAt` extends the same timeline with a run out to full.
 *
 * The whole thing is one animation seeked against wall-clock time, so the bar
 * is a pure function of `startedAt`, `finishAt` and now. Mounting late, or
 * re-rendering, lands on the position the timestamps imply rather than
 * restarting anything.
 */
@customElement('fk-progress')
export class FKProgress extends LitElement {
  /** When the tracked operation began. Defaults to the element's first render. */
  @property({ attribute: false }) startedAt?: number;

  /** When the work is done and the bar should run out to full. */
  @property({ attribute: false }) finishAt?: number;

  /** Seconds until the bar is half of `ceiling` full. Lower fills faster. */
  @property({ type: Number }) halfLife = 4;

  /** The fraction the bar creeps towards and never reaches. */
  @property({ type: Number }) ceiling = 0.98;

  @query('.bar') private _bar!: HTMLDivElement;

  private _animation?: Animation;

  override render() {
    return html`<div class=bar></div>`;
  }

  override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('startedAt') || changedProperties.has('finishAt') ||
        changedProperties.has('halfLife') || changedProperties.has('ceiling'))
      this._restart();
  }

  private _restart() {
    const startedAt = this.startedAt ?? Date.now();
    const creepSpan = this.finishAt === undefined ? CREEP_SPAN : Math.max(0, this.finishAt - startedAt);
    const duration = creepSpan + (this.finishAt === undefined ? 0 : FINISH_DURATION);
    this._animation?.cancel();
    this._animation = this._bar.animate(this._keyframes(creepSpan, duration), {
      duration,
      fill: 'forwards',
    });
    // Seek instead of playing from zero: the operation may have been running
    // long before this element existed.
    this._animation.currentTime = Math.min(duration, Math.max(0, Date.now() - startedAt));
  }

  private _keyframes(creepSpan: number, duration: number): Keyframe[] {
    const keyframes: Keyframe[] = [];
    for (let i = 0; i <= SAMPLE_COUNT; ++i) {
      const elapsed = creepSpan * (i / SAMPLE_COUNT) ** 2;
      const seconds = elapsed / 1000;
      const filled = this.ceiling * seconds / (seconds + this.halfLife);
      // The bar spans the full track and is clipped by it, so the visible
      // portion is whatever has not been translated out to the left.
      keyframes.push({
        offset: duration === 0 ? 0 : elapsed / duration,
        transform: `translateX(${(filled - 1) * 100}%)`,
        easing: 'linear',
      });
    }
    if (this.finishAt === undefined)
      return keyframes;
    // Ease the closing move so the bar decelerates into full rather than
    // slamming there off the back of the linear creep.
    keyframes[keyframes.length - 1].easing = 'ease-out';
    keyframes.push({ offset: 1, transform: 'translateX(0)' });
    return keyframes;
  }

  static styles = [css`
    :host {
      display: block;
      overflow: hidden;
      width: 140px;
      height: 4px;
      border-radius: 999px;
      background: var(--fk-progress-track-color, var(--sl-color-neutral-200));
    }

    .bar {
      width: 100%;
      height: 100%;
      border-radius: inherit;
      background: var(--fk-progress-color, var(--sl-color-primary-500));
      transform: translateX(-100%);
    }
  `];
}
